import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Descendant, Element, Node, Operation, NodeEntry } from 'slate';
import { CustomEditor } from 'slate/types/slate';
import { EditorDocument, SectionEntry, SectionTargetLanguage } from 'entities/document';
import { invalidateOnValueSave } from 'entities/tag';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { v4 as uuidv4 } from 'uuid';
import produce from 'immer';
import { childFinder, changedNodesFromOperations } from 'slate/utils';
import { afterSectionSave } from 'slate/extensions/tag/splitHandlers';
import { isTag } from 'slate/extensions/tag';
import { getBlockTagIds } from 'slate/extensions/tag/types/block';
import { api } from 'shared/api/client';
import { hasOrphanedComments } from 'slate/commentAnchors';
import { commentKeys } from 'entities/comment';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useEditorNavigate } from 'app/router/params';
import { i18n } from 'shared/i18n';
import { useSetEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorUiSetters } from 'app/state/contexts/EditorUiContext';
import { isDuplicateTagPlacement, isRevisionMismatch } from 'shared/api/revision-mismatch';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';

const carriesTagMarker = (node: Node): boolean =>
  Element.isElement(node) && (isTag(node) || getBlockTagIds(node).length > 0 || node.children.some(carriesTagMarker));

const TAG_KEYS = ['tags', 'tagId', 'continuedAt'];

const changesTagPlacement = (operation: Operation) => {
  if (operation.type === 'insert_node' || operation.type === 'remove_node') return carriesTagMarker(operation.node);
  if (operation.type === 'set_node') return TAG_KEYS.some((key) => key in operation.properties || key in operation.newProperties);
  if (operation.type === 'split_node' || operation.type === 'merge_node') return TAG_KEYS.some((key) => key in operation.properties);
  return false;
};

const useSave = (
  document: EditorDocument,
  section: SectionEntry,
  sectionRevisionId: string | null | undefined,
  translationId: string | null,
  setLiveSection: (section: SectionEntry) => void
) => {
  const operationsRef = useRef<Operation[]>([]);
  const currentRevisionId = useRef<string | null>(null);
  const documentId = document.superId;
  const sectionId = section.superId;
  const isSaving = useRef(false);
  const inFlight = useRef(false);
  const pendingSave = useRef<[Descendant[], CustomEditor] | null>(null);
  const revisionConflict = useRef(false);
  const queryClient = useQueryClient();
  const { setIsDirty } = useEditorUiSetters();
  const setEditorDocument = useSetEditorDocument();
  const lastSuccessfulSave = useRef<string | null>(null);
  useEffect(() => {
    currentRevisionId.current = section.revisionId;
    lastSuccessfulSave.current = section.revisionId;
  }, [section.revisionId]);
  useEffect(() => {
    revisionConflict.current = false;
  }, [sectionId]);
  const navigate = useEditorNavigate();
  const saveRef = useRef<(value: Descendant[], editor: CustomEditor) => void>(() => {});
  const save = useCallback(
    (value: Descendant[], editor: CustomEditor) => {
      if (revisionConflict.current) {
        return;
      }
      if (inFlight.current) {
        pendingSave.current = [value, editor];
        return;
      }
      inFlight.current = true;
      const revisionId = uuidv4();
      const baseRevisionId = currentRevisionId.current;
      const operations = operationsRef.current;

      const evolvedValue = produce(value, (draft) => {
        const childFromPath = childFinder(draft);
        // Create new revision IDs for Slate leafs
        changedNodesFromOperations(editor, operations).forEach(([, path]: NodeEntry) => {
          const child = childFromPath(path);
          child.previousRevisionId = child.revisionId;
          child.revisionId = revisionId;
        });
      });

      // Create new section, with same revisionId as the updated slate leafs
      const newSection = {
        ...section,
        previousRevisionId: baseRevisionId,
        revisionId,
        content: evolvedValue,
        createdAt: undefined,
        documentSuperId: documentId,
      };

      // Reset list of operations
      operationsRef.current = [];

      function saveSection() {
        return api.POST('/documents/{superId}/actions', {
          params: { path: { superId: Number(documentId) } },
          body: {
            action: 'update_section',
            revisionId: baseRevisionId,
            data: newSection,
          },
        });
      }

      function saveLang() {
        const sectionRevisionIdToSave =
          sectionRevisionId ||
          document.content.sections.find((section: SectionEntry) => section.targetLanguages?.map((l: SectionTargetLanguage) => l.superId).includes(sectionId))
            ?.revisionId;

        if (!sectionRevisionIdToSave) {
          return Promise.reject(new Error(`No parent section found for target language ${sectionId}`));
        }

        return api
          .POST('/sections/', { body: newSection })
          .then((res) => res.data)
          .then(() => {
            return api.POST('/documents/{superId}/actions', {
              params: { path: { superId: Number(documentId) }, query: { compileSass: 'true' } },
              body: {
                action: 'update_section_target_lang',
                revisionId: baseRevisionId,
                data: {
                  parentRevisionId: sectionRevisionIdToSave,
                  targetLanguage: {
                    revisionId: revisionId,
                    superId: newSection.superId,
                  },
                },
              },
            });
          });
      }

      currentRevisionId.current = revisionId;
      (translationId ? saveLang() : saveSection())
        .then((res: { data: unknown }) => res.data)
        .then((response: unknown) => {
          const { deadTagIds, ...doc } = response as EditorDocument & { deadTagIds?: string[] };
          lastSuccessfulSave.current = revisionId;
          setLiveSection(newSection);
          setEditorDocument(doc as EditorDocument);
          afterSectionSave(editor, deadTagIds);
          if (!pendingSave.current) {
            setIsDirty(false);
            isSaving.current = false;
          }
          invalidateOnValueSave(queryClient, documentId);
          if (hasOrphanedComments(queryClient.getQueryData(commentKeys.list(sectionId)), evolvedValue)) {
            queryClient.invalidateQueries({ queryKey: commentKeys.list(sectionId) });
          }
        })
        .catch((e: unknown) => {
          // save failed, roll back to previous ID
          currentRevisionId.current = lastSuccessfulSave.current;
          operationsRef.current = [...operations, ...operationsRef.current];
          if (isRevisionMismatch(e)) {
            revisionConflict.current = true;
            pendingSave.current = null;
            dispatchMessage(EventType.ADD_MESSAGE, {
              type: 'error',
              title: i18n('document:save-conflict.title'),
              text: i18n('document:save-conflict.message'),
            });
            return;
          }
          if (isDuplicateTagPlacement(e)) {
            revisionConflict.current = true;
            pendingSave.current = null;
            dispatchMessage(EventType.ADD_MESSAGE, {
              type: 'error',
              title: i18n('document:save-duplicate-placement.title'),
              text: i18n('document:save-duplicate-placement.message'),
            });
            return;
          }
          ServerErrorHandler(e);
        })
        .finally(() => {
          inFlight.current = false;
          const next = pendingSave.current;
          pendingSave.current = null;
          if (next) {
            saveRef.current(...next);
          }
        });
    },
    [document, section, documentId, queryClient, sectionId, sectionRevisionId, translationId, setEditorDocument, setIsDirty, setLiveSection]
  );
  saveRef.current = save;
  const [storeSection, , flushSection] = useDelayedSave(save);
  const adoptRevision = useCallback((revisionId: string) => {
    currentRevisionId.current = revisionId;
    lastSuccessfulSave.current = revisionId;
  }, []);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      if (isSaving.current) {
        window.alert(i18n('document:not-saved.warning-message'));
        if (translationId !== null) {
          navigate({ search: (prev) => ({ ...prev, translation: translationId }) });
        } else {
          navigate({ search: (prev) => ({ ...prev, translation: undefined }) });
        }
      } else {
        window.location.reload();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (revisionConflict.current) return;
      const flushed = flushSection();
      if (!flushed && !isSaving.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flushSection]);

  const storeChanges = (newValue: Descendant[], editor: CustomEditor) => {
    const mutatingOperations = editor.operations.filter((operation) => operation.type !== 'set_selection');
    if (mutatingOperations.length > 0) {
      operationsRef.current.push(...mutatingOperations);
      setIsDirty(true);
      isSaving.current = true;
      storeSection(newValue, editor);
      if (mutatingOperations.some(changesTagPlacement)) flushSection();
    }
  };
  return [storeChanges, adoptRevision] as const;
};
export default useSave;
